import React from "react"
import { createBrowserRouter, Navigate, useNavigate } from "react-router-dom"
import { observer } from "mobx-react"

import { authStore } from "@src/app/authStore"
import { Main } from "@src/app/components/Main"
import { paths } from "@src/app/paths"
import UiStore from "@src/app/uiStore"
import { NoMatch } from "@src/pages/404"
import { AffiliateProgram } from "@src/pages/affiliateProgram"
import { AffiliateProgramRequest } from "@src/pages/affiliateProgramRequest"
import { AuthPage } from "@src/pages/auth"
import { BalancesPage } from "@src/pages/balances"
import { BankDetailsSlug } from "@src/pages/bankDetailsSlug"
import { CheckoutsLayout, CheckoutsPage } from "@src/pages/checkouts"
import { CheckoutUpsertPage } from "@src/pages/checkoutUpsert"
import { ConvertPage } from "@src/pages/convert"
import { ConvertConfirmation } from "@src/pages/convertConfirmation"
import { DepositPage } from "@src/pages/deposit"
import { DepositFiatPage } from "@src/pages/depositFiat"
import { EmailPictures } from "@src/pages/emailPictures"
import { KybPage } from "@src/pages/kyb"
import { KybInnerPage } from "@src/pages/kybInner"
import { MassPayoutsPage } from "@src/pages/massPayouts"
import { MassPayoutPageVariants } from "@src/pages/massPayouts/constants"
import { OtpConfirmPage } from "@src/pages/otpConfirm"
import { PasswordChangePage } from "@src/pages/passwordChange"
import { PasswordRecoveryPage } from "@src/pages/passwordRecovery"
import { PasswordRecoveryConfirmPage } from "@src/pages/passwordRecoveryConfirm"
import { PaymentLinkPage } from "@src/pages/paymentLink"
import { PaymentLinksPage } from "@src/pages/paymentLinks"
import { PaymentLinksCreatePage } from "@src/pages/paymentLinksCreate"
import { PaymentsPage } from "@src/pages/payments"
import { PayoutsPage } from "@src/pages/payouts"
import { PayoutsConfirmationPage } from "@src/pages/payoutsConfirmation"
import { ProjectPage } from "@src/pages/project"
import { ProjectsPage } from "@src/pages/projects"
import { ProjectUpsertPage } from "@src/pages/projectUpsert"
import { RefundPage } from "@src/pages/refund"
import { RegistrationPage } from "@src/pages/registration"
import { RegistrationConfirmPage } from "@src/pages/registrationConfirm"
import { ReportsPage } from "@src/pages/reports"
import { SettingsPage } from "@src/pages/settings"
import { SettingsOTP } from "@src/pages/settingsOTP"
import { StatusConfirmation } from "@src/pages/statusConfirmation"
import { SubMerchSlug } from "@src/pages/subMerchSlug"
import { SwapDetails } from "@src/pages/SwapDetails"
import { ThankYou } from "@src/pages/thankYou"
import { TransactionsPage } from "@src/pages/transactions"
import { WithdrawPage } from "@src/pages/withdraw"
import { WIthdrawDetailsPage } from "@src/pages/withdrawDetails"
import { WithdrawFiatPage } from "@src/pages/withdrawFiat"
import { SignupDetailsRequestUserTypeEnum } from "@src/shared/api/generated"
import { MobileScreen } from "@src/shared/componemts/MobileScreen"
import { MerchantStats } from "@src/widgets/merchantStats/ui"
import { WidgetModifyPage } from "./widgetModify"
import { WidgetsPage } from "./widgets"

export const RequireMerchant: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const { isAnonymous, merchant } = authStore

  if (isAnonymous) return <Navigate to={paths.auth} />

  return <>{children}</>
})
export const CheckMerchant: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const { isAnonymous, merchant } = authStore
  const { hostname, pathname } = window.location
  const navigate = useNavigate()

  if (isAnonymous || !merchant) {
    return <Navigate to={paths.auth} />
  }

  const { is_confirmed, submerchant } = merchant
  const needSmsConfirm =
    !is_confirmed || (submerchant && submerchant.status !== "confirmed")

  if (needSmsConfirm) {
    const email = merchant?.submerchant?.email || merchant?.email

    return <Navigate to={`${paths.registrationConfirm}?email=${email}`} />
  }

  if (
    merchant?.user_type === "merchant" &&
    merchant?.details &&
    ["pending", "unconfirmed"].includes(merchant.status) &&
    ![
      "dashboard.sandbox.cryptix.io",
      "dashboard.sandbox.transferio.io",
    ].includes(hostname)
  ) {
    return <Navigate to={paths.statusConfirmation} />
  }

  if (merchant.user_type === "referrer") {
    return <Navigate to={paths.affiliateProgram} />
  }

  if (pathname === paths.default) {
    return <Navigate to={paths.balances} />
  }

  return <>{children}</>
})
export const RequireAnonymous: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const { isAnonymous } = authStore

  if (!isAnonymous) return <Navigate to={paths.default} />

  return <>{children}</>
})

export const CheckMobile: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
    navigator.userAgent,
  )

  if (isMobile) {
    return <MobileScreen></MobileScreen>
  }

  return <>{children}</>
})

export const RequireNotConfirm: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const { isConfirmedMerch } = authStore

  if (isConfirmedMerch) return <Navigate to={paths.default} />

  return <>{children}</>
})

export const RequireEmailNotConfirm: React.FC<
  React.PropsWithChildren & {
    anonymous?: boolean
  }
> = observer(({ children }) => {
  const { isMerchantEmailConfirmed } = authStore

  if (isMerchantEmailConfirmed) return <Navigate to={paths.default} />

  return <>{children}</>
})
export const router = createBrowserRouter([
  {
    element: (
      <RequireAnonymous>
        <AuthPage />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Authorization"),
    path: paths.auth,
  },
  {
    element: (
      <RequireAnonymous>
        <RegistrationPage />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Registration"),
    path: paths.registration,
  },
  {
    element: (
      <RequireAnonymous>
        <RegistrationPage
          forceUserType={SignupDetailsRequestUserTypeEnum.Merchant}
        />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Registration – Cryptix"),
    path: paths.registrationMerch,
  },
  {
    element: (
      <RequireAnonymous>
        <RegistrationPage
          forceUserType={SignupDetailsRequestUserTypeEnum.Referrer}
        />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Registration – Cryptix"),
    path: paths.registrationRef,
  },
  {
    element: (
      <RequireAnonymous>
        <PasswordRecoveryPage />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Recover password"),
    path: paths.recover,
  },
  {
    element: (
      <RequireAnonymous>
        <PasswordRecoveryConfirmPage />
      </RequireAnonymous>
    ),
    loader: () => setTitle("Confirm recover password"),
    path: paths.recoverConfirm,
  },
  {
    element: (
      <RequireAnonymous>
        <Navigate to={paths.auth} />
      </RequireAnonymous>
    ),
    path: "*",
  },
  {
    children: [
      {
        element: <ReportsPage />,
        loader: () => setTitle("Reports"),
        path: paths.reports,
      },
      {
        element: <TransactionsPage />,
        loader: () => setTitle("Transactions"),
        path: paths.transactions,
      },

      {
        children: [
          {
            element: <CheckoutsPage />,
            index: true,
          },
          {
            element: <CheckoutUpsertPage />,
            loader: () => setTitle("Checkout"),
            path: paths.checkout,
          },
        ],
        element: <CheckoutsLayout />,
        loader: () => setTitle("Checkouts"),
        path: paths.checkouts,
      },
      {
        element: <PaymentLinksPage />,
        loader: () => setTitle("Payment Links"),
        path: paths.paymentLinks,
      },
      {
        element: <PaymentLinksCreatePage />,
        loader: () => setTitle("Create Payment Link"),
        path: paths.paymentLinksCreate,
      },
      {
        element: <PaymentLinkPage />,
        loader: () => setTitle("Payment Link"),
        path: paths.paymentLinkShow,
      },
      {
        element: <ProjectPage />,
        loader: () => setTitle("Project"),
        path: paths.project,
      },
      {
        element: <ProjectsPage />,
        loader: () => setTitle("Projects"),
        path: paths.projects,
      },
      {
        element: <WidgetsPage />,
        loader: () => setTitle("My Widgets"),
        path: paths.widgets,
      },
      {
        element: <WidgetModifyPage />,
        loader: ({ params: { id } }) => {
          const title = id === "new" ? `Create New Widget` : `Edit Widget`

          return setTitle(title)
        },
        path: paths.widgetModify,
      },
      {
        element: <ProjectUpsertPage />,
        loader: ({ params: { id } }) => {
          const title = id === "new" ? `Create New Project` : `Edit Project`

          return setTitle(title)
        },
        path: paths.projectUpsert,
      },
      {
        element: <SwapDetails />,
        loader: ({ params: { id } }) => {
          return setTitle("Swap")
        },
        path: paths.swapDetails,
      },
      {
        element: <BalancesPage />,
        loader: () => setTitle("Balances"),
        path: paths.balances,
      },
      {
        element: <MerchantStats />,
        loader: () => setTitle("Analytics"),
        path: paths.analytics,
      },
      {
        element: <SettingsPage />,
        loader: () => setTitle("Settings"),
        path: paths.settings,
      },
      {
        element: <SubMerchSlug />,
        loader: () => setTitle("Sub merchant"),
        path: paths.subMerchDetail,
      },
      {
        element: <PasswordChangePage />,
        loader: () => setTitle("Change password"),
        path: paths.passwordChange,
      },
      {
        element: <SettingsOTP />,
        loader: () => setTitle("OTP"),
        path: paths.otp,
      },
      {
        element: <KybPage />,
        loader: () => setTitle("KYC"),
        path: paths.kyb,
      },
      {
        element: <KybInnerPage />,
        loader: ({ params: { id } }) => setTitle(`KYC level ${id}`),
        path: paths.kybInner,
      },
      {
        element: <ConvertPage />,
        loader: () => setTitle("Convert"),
        path: paths.swap,
      },
      {
        element: <DepositPage />,
        loader: () => setTitle("Deposit"),
        path: paths.deposit,
      },
      {
        element: <DepositFiatPage />,
        loader: () => setTitle("Deposit"),
        path: paths.depositFiat,
      },
      {
        element: <WithdrawPage />,
        loader: () => setTitle("Withdraw"),
        path: paths.withdraw,
      },
      {
        element: <WithdrawFiatPage />,
        loader: () => setTitle("Withdraw"),
        path: paths.withdrawFiat,
      },
      {
        element: <WIthdrawDetailsPage />,
        loader: () => setTitle("Withdraw Details"),
        path: paths.withdrawDetails,
      },
      {
        element: <MassPayoutsPage />,
        loader: () => setTitle("Payouts"),
        path: paths.massPayouts,
      },
      {
        element: <OtpConfirmPage />,
        loader: () => setTitle("Confirm OTP"),
        path: paths.confirmOTP,
      },
      {
        element: <PaymentsPage />,
        loader: () => setTitle("Payments"),
        path: paths.payments,
      },
      {
        element: <PayoutsPage view={MassPayoutPageVariants.Cards} />,
        loader: () => setTitle("Payouts"),
        path: paths.payoutsCard,
      },
      {
        element: <PayoutsPage view={MassPayoutPageVariants.Crypto} />,
        loader: () => setTitle("Payouts"),
        path: paths.payouts,
      },
      {
        element: <PayoutsConfirmationPage />,
        loader: () => setTitle("Payouts Confirmation"),
        path: paths.payoutsConfirmation,
      },
      {
        element: <RefundPage />,
        loader: () => setTitle("Refund"),
        path: paths.refund,
      },
      {
        element: <EmailPictures />,
        loader: () => setTitle("Pictures"),
        path: paths.emailPictures,
      },
      {
        element: <ConvertConfirmation />,
        loader: () => setTitle("Confirmation"),
        path: paths.convertConfirmation,
      },
      {
        element: <BankDetailsSlug />,
        loader: () => setTitle("Bank Details"),
        path: paths.bankDetailSlug,
      },
    ],
    element: (
      <CheckMerchant>
        <CheckMobile>
          <Main />
        </CheckMobile>
      </CheckMerchant>
    ),
    path: "/",
  },
  {
    element: (
      <RequireMerchant>
        <RequireEmailNotConfirm>
          <RegistrationConfirmPage />
        </RequireEmailNotConfirm>
      </RequireMerchant>
    ),
    loader: () => setTitle("Confirm registration"),
    path: paths.registrationConfirm,
  },
  {
    element: (
      <RequireMerchant>
        <RequireNotConfirm>
          <StatusConfirmation />
        </RequireNotConfirm>
      </RequireMerchant>
    ),
    loader: () => setTitle("Confirmation"),
    path: paths.statusConfirmation,
  },
  {
    element: (
      <RequireMerchant>
        <ThankYou />
      </RequireMerchant>
    ),
    loader: () => setTitle("Thank you"),
    path: paths.thankYou,
  },
  {
    element: (
      <RequireMerchant>
        <CheckMobile>
          <AffiliateProgram />
        </CheckMobile>
      </RequireMerchant>
    ),
    loader: () => setTitle("Affiliate Program"),
    path: paths.affiliateProgram,
  },
  {
    element: (
      <RequireMerchant>
        <AffiliateProgramRequest />
      </RequireMerchant>
    ),
    loader: () => setTitle("Affiliate Program"),
    path: paths.affiliateProgramRequest,
  },
  {
    element: <NoMatch />,
    loader: () => setTitle("404"),
    path: "*",
  },
])
function setTitle(title: string) {
  document.title = `${title} - ${UiStore().theme}`

  return null
}
